import { StrictMode, useEffect as ReactUseEffect, useMemo } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import { ChatProvider } from './context/ChatContext'
import { createChatClient } from './lib/chatApi'
import {
  appendTestSessionId,
  computeThemeUrl,
  isValidThemeCssUrl,
  persistTestConfig,
  persistTestSessionId,
  updateGlobalTestConfig,
} from './embed/config'
import { StoreProvider, useStore } from './context/StoreContext'
import { LocaleProvider } from './context/LocaleContext'

// --- SPA runtime backend selection (hardcoded DEV/PROD), default PROD ---
const SPA_BACKEND_KEY = 'ww/spa/backendMode' as const;
type BackendMode = 'dev' | 'prod';

function getDefaultMode(): BackendMode {
  return 'prod';
}

function getBackendMode(): BackendMode {
  try {
    const q = new URL(window.location.href).searchParams.get('backend');
    if (q === 'dev' || q === 'prod') {
      try { localStorage.setItem(SPA_BACKEND_KEY, q); } catch {}
      return q;
    }
  } catch {}
  try {
    const saved = localStorage.getItem(SPA_BACKEND_KEY);
    if (saved === 'dev' || saved === 'prod') return saved as BackendMode;
  } catch {}
  return getDefaultMode();
}

function getOrigins(mode: BackendMode): { origin: string; chatBase: string; initUrl: string } {
  const PROD = 'https://wandway-backend.onrender.com';
  const DEV = 'https://wandway-back.onrender.com';
  const origin = (mode === 'prod' ? PROD : DEV).replace(/\/$/, '');
  return {
    origin,
    chatBase: `${origin}/chat`,
    initUrl: `${origin}/widget/init`,
  };
}

// Hoist Google Fonts @import to document <head> and dedupe link tags
const _wwFontLinks = new Set<string>();
function ensureHeadLink(href: string, rel: 'preconnect' | 'stylesheet', opts?: { crossorigin?: string }) {
  const key = `${rel}:${href}`;
  if (_wwFontLinks.has(key) || document.head.querySelector(`link[rel="${rel}"][href="${href}"]`)) return;
  const l = document.createElement('link');
  l.rel = rel;
  l.href = href;
  if (opts?.crossorigin) l.crossOrigin = opts.crossorigin;
  document.head.appendChild(l);
  _wwFontLinks.add(key);
}

function hoistGoogleFontImports(cssText: string): string {
  const importRe = /@import\s+(?:url\(\s*)?(?:"|')?(https:\/\/fonts\.googleapis\.com\/[^"')\s]+)(?:"|')?\s*\)?\s*;/gi;
  const links: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = importRe.exec(cssText)) !== null) {
    if (m[1]) links.push(m[1]);
  }
  if (links.length === 0) return cssText;

  // Best-practice preconnects
  ensureHeadLink('https://fonts.gstatic.com', 'preconnect', { crossorigin: 'anonymous' });
  ensureHeadLink('https://fonts.googleapis.com', 'preconnect');

  // Inject stylesheet links (ensure display=swap)
  for (const href of links) {
    try {
      const url = new URL(href, window.location.href);
      if (!url.searchParams.get('display')) url.searchParams.set('display', 'swap');
      ensureHeadLink(url.toString(), 'stylesheet');
    } catch {
      // ignore malformed URLs
    }
  }

  // Remove @import rules from inlined CSS
  return cssText.replace(importRe, '');
}

let spaThemeSheet: CSSStyleSheet | null = null;
async function applySpaTheme(storeId: string): Promise<void> {
  try {
    const MODE = getBackendMode();
    const { initUrl } = getOrigins(MODE);
    const url = `${initUrl}?store_id=${encodeURIComponent(storeId)}`;
    const urlWithSession = appendTestSessionId(url);
    const res = await fetch(urlWithSession, { method: 'GET', credentials: 'omit', cache: 'no-store', mode: 'cors' });
    let themeCssUrl = computeThemeUrl(storeId);
    if (res.ok) {
      try {
        const data = await res.json();
        persistTestSessionId(data.test_session_id);
        persistTestConfig(data.test_config);
        updateGlobalTestConfig({ testSessionId: data.test_session_id, testConfig: data.test_config });
        const candidate = (data?.theme_css_url ?? '').trim();
        if (candidate && isValidThemeCssUrl(candidate)) {
          themeCssUrl = candidate;
        }
      } catch {}
    }

    // Fetch theme CSS and adapt to SPA by mapping :host -> :root
    const cssRes = await fetch(themeCssUrl, { method: 'GET', credentials: 'omit', cache: 'no-store', mode: 'cors' });
    if (!cssRes.ok) return;
    let cssText = await cssRes.text();
    cssText = hoistGoogleFontImports(cssText);
    cssText = cssText.replace(/:host\b/g, ':root');

    const supportsConstructable = typeof (window as any).CSSStyleSheet !== 'undefined'
      && !!(CSSStyleSheet.prototype as any).replaceSync
      && !!(document as any).adoptedStyleSheets;

    if (supportsConstructable) {
      if (!spaThemeSheet) spaThemeSheet = new CSSStyleSheet();
      spaThemeSheet.replaceSync(cssText);
      const current = ((document as any).adoptedStyleSheets || []) as CSSStyleSheet[];
      if (!current.includes(spaThemeSheet)) {
        (document as any).adoptedStyleSheets = [...current, spaThemeSheet];
      }
    } else {
      let styleEl = document.getElementById('ww-spa-theme') as HTMLStyleElement | null;
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = 'ww-spa-theme';
        document.head.appendChild(styleEl);
      }
      styleEl.textContent = cssText;
    }

    document.documentElement.classList.add('ww-spa');
  } catch {
    // Non-fatal: leave defaults
  }
}

function SpaApp() {
  const { storeId } = useStore()

  const client = useMemo(() => {
    const refreshToken = async () => {
      try {
        const MODE = getBackendMode();
        const { initUrl, chatBase } = getOrigins(MODE);
        const url = `${initUrl}?store_id=${encodeURIComponent(storeId)}`
        const urlWithSession = appendTestSessionId(url);
        const res = await fetch(urlWithSession, { 
          method: 'GET', 
          credentials: 'omit', 
          cache: 'no-store',
          mode: 'cors' // Ensure Origin header is sent
        })
        if (!res.ok) return null
        const data = await res.json()
        persistTestSessionId(data.test_session_id);
        persistTestConfig(data.test_config);
        updateGlobalTestConfig({ testSessionId: data.test_session_id, testConfig: data.test_config });
        const base = (data.api_base || '').replace(/\/$/, '')
        // Force HTTPS for PROD path to avoid mixed content if backend returns http
        const secureBase = (MODE === 'prod') ? base.replace(/^http:\/\//i, 'https://') : base
        const fixedApiBase = /\/chat$/.test(secureBase) ? secureBase : `${secureBase}/chat`
        const nextToken = data.token as string | undefined
        if (!nextToken) return null
        // Use selected mode: dev uses hardcoded dev chat base; prod uses backend-provided base
        return (MODE === 'prod')
          ? { token: nextToken, apiBase: fixedApiBase }
          : { token: nextToken, apiBase: chatBase }
      } catch {
        return null
      }
    }

    const { chatBase } = getOrigins(getBackendMode());
    return createChatClient({ apiBase: chatBase, storeId, refreshToken });
  }, [storeId])

  // Apply the widget theme to the SPA so visuals match production widgets
  ReactUseEffect(() => {
    applySpaTheme(storeId)
  }, [storeId])

  return (
    <ChatProvider key={storeId} client={client}>
      <App />
    </ChatProvider>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <StoreProvider>
      <LocaleProvider>
        <SpaApp />
      </LocaleProvider>
    </StoreProvider>
  </StrictMode>,
)
